import { slateNodesToInsertDelta, yTextToSlateElement } from '@slate-yjs/core'
import type { Descendant } from 'slate'
import * as Y from 'yjs'

export function createRevertUpdateFromSlate(doc: Y.Doc, body: Descendant[]) {
  // Create a doc with the given content
  const tmpDoc = new Y.Doc()
  const yText = tmpDoc.get('content', Y.XmlText)
  slateElementToYText(body, yText)

  return encodeRevertedStateAsUpdate(doc, tmpDoc)
}

function encodeRevertedStateAsUpdate(doc: Y.Doc, oldDoc: Y.Doc) {
  const currentStateVector = Y.encodeStateVector(doc)
  const snapshotStateVector = Y.encodeStateVector(oldDoc)

  // Create update with changes between snapshot and now
  const diffUpdate = Y.encodeStateAsUpdate(doc, snapshotStateVector)

  // Track changes with UndoManager, applying and then reverting update
  const undoManager = new Y.UndoManager(oldDoc.get('content', Y.XmlText))
  Y.applyUpdate(oldDoc, diffUpdate)
  undoManager.undo()

  // returning an update, that reverts the changes since the given snapshot
  return Y.encodeStateAsUpdate(oldDoc, currentStateVector)
}

export function slateElementToYText(
  element: Descendant | Descendant[],
  yText?: Y.XmlText,
): Y.XmlText {
  yText = yText ?? new Y.XmlText()
  const elements = Array.isArray(element) ? element : [element]

  try {
    const delta = slateNodesToInsertDelta(elements)
    yText.applyDelta(delta, { sanitize: false })
  } catch (error) {
    console.error('Error applying delta:', error)
  }

  return yText
}

export function slateBodyToYDoc(body: Descendant[]) {
  const doc = new Y.Doc()
  const yText = doc.get('content', Y.XmlText)
  slateElementToYText(body, yText)

  return doc
}

/**
 * Merges updates into one update without the content of deleted items.
 * Unlike `Y.mergeUpdates`, the result doesn't grow with the editing history.
 */
export function mergeAndCompactUpdates(
  updates: (Uint8Array | null | undefined)[],
  { dropPendingEdits = false }: { dropPendingEdits?: boolean } = {},
) {
  const doc = new Y.Doc()
  // Yjs cleans up formatting after applying remote updates, which would add
  // deletions no client made and change the formatting of concurrent edits
  doc.on('afterTransaction', (transaction) => {
    transaction._needFormattingCleanup = false
  })

  for (const update of updates) {
    if (update?.byteLength) Y.applyUpdate(doc, update)
  }

  if (dropPendingEdits) {
    // Pending edits depend on updates that never arrived. They aren't visible
    // and are lost for good if the missing updates arrive later.
    doc.store.pendingStructs = null
    doc.store.pendingDs = null
  }

  return Y.encodeStateAsUpdate(doc)
}

/**
 * Size of the edits that can't be applied because updates they depend on are missing.
 */
export function getPendingEditsSize(update: Uint8Array | null) {
  if (!update?.byteLength) return 0

  const doc = new Y.Doc()
  Y.applyUpdate(doc, update)

  const pendingStructsSize = doc.store.pendingStructs?.update.byteLength ?? 0
  const pendingDeletionsSize = doc.store.pendingDs?.byteLength ?? 0

  return pendingStructsSize + pendingDeletionsSize
}

export function yDocToSlateElement(update: Uint8Array | null): Descendant[] {
  if (!update) return []
  if (update.byteLength === 0) return []
  const fullDoc = new Y.Doc()
  Y.applyUpdate(fullDoc, update)
  const content = fullDoc.get('content', Y.XmlText)

  return yTextToSlateElement(content).children
}
